<template>
    <section
        class="flex flex-row gap-2"
        :class="{
            'bg-emerald-100 -mx-1.5 px-1.5 -mt-1.5 pt-1.5 rounded-sm': props.hasUnsavedChanges === 'New',
            'bg-sky-100 -mx-1.5 px-1.5 -mt-1.5 pt-1.5 rounded-sm': props.hasUnsavedChanges === 'Edited',
            'bg-red-100 -mx-1.5 px-1.5 -mt-1.5 pt-1.5 rounded-sm': DELETED_STATES.includes(props.hasUnsavedChanges),
        }"
    >
        <div class="flex flex-col items-center">
            <div class="w-8 h-8 shrink-0 rounded-full relative">
                <Tooltip :is-disabled="!hasLoadingImageFailed">
                    <template #trigger="tooltipProps">
                        <img
                            v-if="imageUrl"
                            alt=""
                            class="object-cover w-8 h-8 rounded-full focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-red-700"
                            :class="{
                                'border-4 border-red-700 bg-red-200': hasLoadingImageFailed,
                                grayscale: DELETED_STATES.includes(props.hasUnsavedChanges),
                            }"
                            :src="props.imageUrl"
                            :tabindex="hasLoadingImageFailed ? 0 : -1"
                            :aria-labelledby="hasLoadingImageFailed ? tooltipProps['aria-labelledby'] : headlineId"
                            :popovertarget="tooltipProps['popovertarget'] ? tooltipProps.popovertarget : undefined"
                            @error="onImageError"
                        />
                        <InitialsWithBackground
                            v-else
                            :first-name="props.firstName"
                            :last-name="props.lastName"
                            class="size-8 leading-8"
                        />
                    </template>

                    <template #text>
                        <div class="w-max">
                            {{ $t('loading-avatar-failed', { name: `${props.firstName} ${props.lastName}` }) }}
                        </div>
                    </template>
                </Tooltip>

                <div
                    class="bg-gray-50 text-gray-600 flex place-items-center place-content-center rounded-tl w-3.5 h-3.5 absolute bottom-0 right-0"
                >
                    <CalendarEditIcon class="size-3" />
                </div>
            </div>

            <div
                class="border-l border-l-solid h-full w-px mt-2 translate-x-[0.5px]"
                :class="{
                    'mb-2': props.hasUnsavedChanges !== 'None',
                    'border-l-gray-300': props.hasUnsavedChanges === 'None',
                    'border-l-emerald-200': props.hasUnsavedChanges === 'New',
                    'border-l-sky-200': props.hasUnsavedChanges === 'Edited',
                    'border-l-red-200': DELETED_STATES.includes(props.hasUnsavedChanges),
                }"
            />
        </div>

        <div class="flex flex-col w-full pb-2">
            <div class="flex flex-row justify-between items-start">
                <div class="flex flex-col">
                    <h3 :id="headlineId" class="font-medium text-gray-700 flex flex-row gap-1 items-center">
                        <router-link
                            :to="props.userPageLink"
                            class="active:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 cursor-pointer hover:underline rounded-xs leading-none"
                            :class="{ 'line-through': DELETED_STATES.includes(props.hasUnsavedChanges) }"
                        >
                            {{ props.firstName }} {{ props.lastName }}
                        </router-link>

                        <span v-if="props.hasUnsavedChanges === 'New'" class="font-light text-emerald-600 text-sm">
                            {{ $t('temporarily-new-comment') }}
                        </span>
                        <span v-if="props.hasUnsavedChanges === 'Edited'" class="font-light text-sky-600 text-sm">
                            {{ $t('temporarily-edited-comment') }}
                        </span>
                        <span
                            v-if="DELETED_STATES.includes(props.hasUnsavedChanges)"
                            class="font-light text-red-600 text-sm"
                        >
                            {{ $t('temporarily-marked-for-deletion-comment') }}
                        </span>
                    </h3>
                    <time
                        :datetime="dateTime"
                        class="text-xs font-light text-gray-500"
                        :class="{ 'line-through': DELETED_STATES.includes(props.hasUnsavedChanges) }"
                    >
                        {{ props.dateTime.toLocaleString() }}
                    </time>
                </div>

                <div v-if="props.isEditable" class="flex flex-row gap-1.5">
                    <Button
                        v-if="!isInEditMode"
                        ref="editButtonRef"
                        size="Small"
                        variant="Info-Strong"
                        :icon-description="$t('edit-comment')"
                        :disabled-explanation="
                            DELETED_STATES.includes(props.hasUnsavedChanges) ? $t('marked-for-deletion') : undefined
                        "
                        @click="isInEditMode = true"
                    >
                        <template #icon>
                            <EditIcon class="size-4.5" />
                        </template>
                    </Button>

                    <Button
                        v-if="isInEditMode"
                        ref="abortButtonRef"
                        size="Small"
                        :variant="hasChanges ? 'Danger-Light' : 'Info-Light'"
                        @click="((commentValue = ''), (isInEditMode = false))"
                    >
                        <template #icon>
                            <UndoIcon class="size-4.5" />
                        </template>

                        <template v-if="!hasChanges">{{ $t('stop-editing-comment') }}</template>
                        <template v-if="hasChanges">{{ $t('discard-comment-changes') }}</template>
                    </Button>

                    <Button
                        v-if="!isInEditMode && !DELETED_STATES.includes(props.hasUnsavedChanges)"
                        size="Small"
                        variant="Danger-Strong"
                        :icon-description="$t('delete-comment')"
                        @click="emit('delete-comment')"
                    >
                        <template #icon>
                            <DeleteForeverIcon class="size-4.5" />
                        </template>
                    </Button>

                    <Button
                        v-if="!isInEditMode && DELETED_STATES.includes(props.hasUnsavedChanges)"
                        size="Small"
                        variant="Info-Strong"
                        :icon-description="$t('undo-marking-for-deletion')"
                        @click="emit('undo-deleting-comment')"
                    >
                        <template #icon>
                            <UndoIcon class="size-4.5" />
                        </template>
                    </Button>
                </div>
            </div>

            <p
                v-if="!isInEditMode"
                class="text-gray-600 text-sm font-light whitespace-break-spaces"
                :class="{ 'line-through': DELETED_STATES.includes(props.hasUnsavedChanges) }"
            >
                {{ props.text }}
            </p>

            <div v-if="isInEditMode" class="flex flex-col gap-2 mt-2">
                <TextAreaField :label="$t('label-comment')" :value="props.text" @input="onCommentChange" />

                <div class="place-self-end">
                    <Button
                        size="Small"
                        variant="Success-Strong"
                        @click="(emit('update-comment', commentValue), (isInEditMode = false))"
                    >
                        <template #icon>
                            <AllDoneIcon class="size-4.5" />
                        </template>
                        {{ $t('save-comment') }}
                    </Button>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { DELETED_STATES, UnsavedChanges } from '@/feed/types';
import InitialsWithBackground from '@/user/components/InitialsWithBackground.vue';
import { useImageError } from '@/utils/useImageError';
import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';
import { computed, ref, watchEffect } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import Button from '../Button/Button.vue';
import TextAreaField from '../Form/TextAreaField.vue';
import AllDoneIcon from '../Icon/AllDoneIcon.vue';
import CalendarEditIcon from '../Icon/CalendarEditIcon.vue';
import DeleteForeverIcon from '../Icon/DeleteForeverIcon.vue';
import EditIcon from '../Icon/EditIcon.vue';
import UndoIcon from '../Icon/UndoIcon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';

type DateCommentEntryProps = {
    imageUrl: string;
    firstName: string;
    lastName: string;
    userPageLink: RouteLocationRaw;
    dateTime: DateTime;
    text: string;
    isEditable: boolean;
    hasUnsavedChanges: UnsavedChanges;
};

type DateCommentEntryEmits = {
    (e: 'delete-comment'): void;
    (e: 'update-comment', value: string): void;
    (e: 'undo-deleting-comment'): void;
};

const props = defineProps<DateCommentEntryProps>();
const emit = defineEmits<DateCommentEntryEmits>();

const headlineId = nanoid();
const isInEditMode = ref(false);
const commentValue = ref('');
const hasChanges = computed(() => commentValue.value !== '' && commentValue.value !== props.text);

const { hasLoadingImageFailed, onImageError } = useImageError(false);

const abortButtonRef = ref();
const editButtonRef = ref();
watchEffect(() => {
    if (isInEditMode.value && abortButtonRef.value) {
        abortButtonRef.value.focus();
    }
});
watchEffect(() => {
    if (!isInEditMode.value && editButtonRef.value) {
        editButtonRef.value.focus();
    }
});

function onCommentChange(event: Event) {
    commentValue.value = (event.target as HTMLInputElement).value;
}

const dateTime = computed(() => {
    const result = props.dateTime.toISODate();

    if (!result) {
        throw new Error('Conversion toISODate() failed');
    }

    return result;
});
</script>
